import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Rarity, RewardConfigRarity } from '#src/__generated__/globalTypes.ts';
import type { Color, HexColorOnly, RGBColor } from '#src/style/types.ts';

import type { getRewardRGBColor_anyRewardConfigInterface } from './__generated__/colors.graphql.ts';
import { isType, isTypes, withFragments } from './gql/index.ts';
import { inGameCurrencyToRarity } from './scarcity.ts';

type Input = (HexColorOnly | Rgb | RgbString)[];

type Item = Url | HTMLImageElement;

type Output = (HexColorOnly | Rgb)[];

type Rgb = [r: number, g: number, b: number];
export type RgbString = `${number}, ${number}, ${number}`;

type Url = string;

export type ColorFormat = HexColorOnly | RgbString | Rgb;

export const isHexColor = (c: Nullable<string>): c is HexColorOnly => {
  return !!c && c.startsWith('#');
};

export const ensureHexColor = (c: Nullable<string>): HexColorOnly => {
  return !!c && isHexColor(c) ? c : '#fff';
};

const getHexFromRGB = (rgb: Rgb): HexColorOnly =>
  `#${rgb
    .map(val => {
      const hex = val.toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    })
    .join('')}`;

const getRGBFromHex = (hex: HexColorOnly): Rgb => {
  let strippedHex = hex.replace('#', '');

  if (strippedHex.length === 3) {
    strippedHex = strippedHex
      .split('')
      .map(char => char + char)
      .join('');
  }

  const r = parseInt(strippedHex.substring(0, 2), 16);
  const g = parseInt(strippedHex.substring(2, 4), 16);
  const b = parseInt(strippedHex.substring(4, 6), 16);

  return [r, g, b];
};

const colorToRgb = (color: ColorFormat): Rgb => {
  if (Array.isArray(color)) {
    return color;
  }
  if (isHexColor(color)) {
    return getRGBFromHex(color);
  }
  return color.split(/,\s*/).map(Number) as Rgb;
};

export const getRgbColorFromHex = (hex: ColorFormat): RgbString => {
  const [r, g, b] = colorToRgb(hex);
  return `${r}, ${g}, ${b}`;
};

export const isLightColor = (color: ColorFormat) => {
  const [r, g, b] = colorToRgb(color);

  // Based on https://stackoverflow.com/a/3943023 There are more precise (and more complex) ways to calculate this: https://stackoverflow.com/a/56678483
  if (r * 0.299 + g * 0.587 + b * 0.114 > 186) {
    return true;
  }
  return false;
};

const getSrc = (item: Item): string =>
  typeof item === 'string' ? item : item.src;

type Args = {
  amount: number;
  format: string;
  group: number;
  sample: number;
};
const getArgs = ({
  amount = 3,
  format = 'array',
  group = 20,
  sample = 10,
} = {}): Args => ({ amount, format, group, sample });

const group = (number: number, grouping: number): number => {
  const grouped = Math.round(number / grouping) * grouping;

  return Math.min(grouped, 255);
};

const format = (input: Input, args: Args): Output => {
  const list = input.map(val => {
    const rgb = Array.isArray(val) ? val : (val.split(',').map(Number) as Rgb);
    return args.format === 'hex' ? getHexFromRGB(rgb) : rgb;
  });

  return list;
};

const getImageData = async (src: Url): Promise<Uint8ClampedArray> =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    const img = new Image();

    img.onload = () => {
      try {
        canvas.height = img.height;
        canvas.width = img.width;
        context.drawImage(img, 0, 0);

        const { data } = context.getImageData(0, 0, img.width, img.height);

        resolve(data);
      } catch {
        reject(Error('Image loading failed.'));
      }
    };
    img.onerror = () => reject(Error('Image loading failed.'));
    img.crossOrigin = '';
    img.src = src;
  });

const getProminent = (data: Uint8ClampedArray, args: Args): Output => {
  const gap = 4 * args.sample;
  const colors: { [key: RgbString]: number } = {};

  for (let i = 0; i < data.length; i += gap) {
    const rgb = [
      group(data[i], args.group),
      group(data[i + 1], args.group),
      group(data[i + 2], args.group),
    ].join(', ') as RgbString;

    colors[rgb] = colors[rgb] ? colors[rgb] + 1 : 1;
  }
  const sortedColors = Object.entries(colors).sort(
    ([, valA], [, valB]) => valB - valA
  ) as [RgbString, number][];
  const prominentColors = sortedColors
    .slice(0, args.amount)
    .map(([rgb]) => rgb);
  return format(prominentColors, args);
};

const process = async (
  handler: (data: Uint8ClampedArray, args: Args) => Output,
  item: Item,
  args?: Partial<Args>
): Promise<Output> => handler(await getImageData(getSrc(item)), getArgs(args));

export const getProminentColor = async (item: Item, args?: Partial<Args>) =>
  process(getProminent, item, args);

const rgbColorPerRarity: Record<Rarity, RGBColor> = {
  [Rarity.common]: 'var(--c-rgb-common)',
  [Rarity.limited]: 'var(--c-rgb-limited)',
  [Rarity.rare]: 'var(--c-rgb-rare)',
  [Rarity.super_rare]: 'var(--c-rgb-superRare)',
  [Rarity.unique]: 'var(--c-rgb-unique)',
  [Rarity.custom_series]: 'var(--c-rgb-customSeries)',
};

const rgbSecondaryColorPerRarity: Record<Rarity, RGBColor> = {
  [Rarity.common]: 'var(--c-rgb-common-secondary-secondary)',
  [Rarity.limited]: 'var(--c-rgb-limited-secondary)',
  [Rarity.rare]: 'var(--c-rgb-rare-secondary)',
  [Rarity.super_rare]: 'var(--c-rgb-superRare-secondary)',
  [Rarity.unique]: 'var(--c-rgb-unique-secondary)',
  [Rarity.custom_series]: 'var(--c-rgb-customSeries)',
};

export const getRarityRGBColor = (rarity: Rarity): RGBColor =>
  rgbColorPerRarity[rarity];

export const getRewardRGBColor = withFragments(
  (
    anyRewardConfig: getRewardRGBColor_anyRewardConfigInterface
  ): RGBColor | null => {
    if (isType(anyRewardConfig, 'InGameCurrencyRewardConfig')) {
      const rarity = inGameCurrencyToRarity[anyRewardConfig.currency];
      return rarity ? getRarityRGBColor(rarity) : null;
    }
    if (
      isTypes(anyRewardConfig, ['CardShardRewardConfig', 'CardRewardConfig'])
    ) {
      return getRarityRGBColor(anyRewardConfig.rarity);
    }
    if (isType(anyRewardConfig, 'MonetaryRewardConfig')) {
      return 'var(--c-rgb-unique)';
    }
    if (isType(anyRewardConfig, 'ConversionCreditRewardConfig')) {
      return 'var(--c-rgb-score-high)';
    }
    return null;
  },
  {
    anyRewardConfigInterface: gql`
      fragment getRewardRGBColor_anyRewardConfigInterface on AnyRewardConfigInterface {
        id
        ... on InGameCurrencyRewardConfig {
          id
          currency
        }
        ... on CardShardRewardConfig {
          id
          rarity
        }
        ... on CardRewardConfig {
          id
          rarity
        }
      }
    ` as TypedDocumentNode<getRewardRGBColor_anyRewardConfigInterface>,
  }
);

export const getRarityColor = (
  rarity: Rarity,
  opts?: { opacity?: number }
): Color => {
  const { opacity = 1 } = opts || {};
  return `rgba(${getRarityRGBColor(rarity)}, ${opacity})`;
};

export const getRaritySecondaryColor = (
  rarity: Rarity,
  opacity: number = 1
): Color => `rgba(${rgbSecondaryColorPerRarity[rarity]}, ${opacity})`;

const gradientsPerRarity: Record<
  Exclude<Rarity, typeof Rarity.custom_series> | 'mix',
  { border: [string, string]; bg: [string, string]; hoverBg: [string, string] }
> = {
  [Rarity.common]: {
    bg: ['#1F1F1F', '#1A1B1C'],
    border: [getRarityColor(Rarity.common), '#1A1B1C'],
    hoverBg: ['#1F1F1F', '#1A1B1C'],
  },
  [Rarity.limited]: {
    bg: ['#1E1706', '#1C1206'],
    border: [getRarityColor(Rarity.limited), '#1C1206'],
    hoverBg: ['#382a0a', '#1C1206'],
  },
  [Rarity.rare]: {
    bg: ['#1C0607', '#120606'],
    border: [getRarityColor(Rarity.rare), '#120606'],
    hoverBg: ['#3b0b0d', '#120606'],
  },
  [Rarity.super_rare]: {
    bg: ['#08151F', '#060C1A'],
    border: [getRarityColor(Rarity.super_rare), '#060C1A'],
    hoverBg: ['#0c2131', '#060C1A'],
  },
  [Rarity.unique]: {
    bg: ['#16071E', '#080609'],
    border: [getRarityColor(Rarity.unique), '#080609'],
    hoverBg: ['#270c36', '#080609'],
  },
  mix: {
    bg: ['#061E1D', '#060C0C'],
    border: ['#01FBF3', '#060C0C'],
    hoverBg: ['#072826', '#060C0C'],
  },
};

export const getEdgeRectangleShapeRarityGradients = (
  rarity: RewardConfigRarity | 'mix'
) => {
  if (rarity === Rarity.custom_series) {
    return {
      bg: '',
      border: '',
    };
  }
  if (rarity === RewardConfigRarity.unknown_rarity) {
    return {
      bg: '',
      border: '',
    };
  }

  return {
    bg: `linear-gradient(to bottom, ${gradientsPerRarity[rarity].bg[0]}, ${gradientsPerRarity[rarity].bg[1]})`,
    border: `linear-gradient(to bottom, ${gradientsPerRarity[rarity].border[0]} 0%, ${gradientsPerRarity[rarity].border[1]} 75%)`,
    hoverBg: `linear-gradient(to bottom, ${gradientsPerRarity[rarity].hoverBg[0]}, ${gradientsPerRarity[rarity].hoverBg[1]})`,
  };
};

// we have difficulty including color into API Competition type, so it's hardcoded for now
export const COMPETITION_COLORS: Record<string, HexColorOnly> = {
  'premier-league-gb-eng': '#F258E3',
  'laliga-es': '#FF563D',
  'bundesliga-de': '#FF0033',
  'ligue-1-fr': '#0061FE',
  nba: '#FF0033',
};
